/* eslint-disable no-console -- Dev-only recording tool that outputs directly to browser console */
/**
 * UAT Trace Recorder
 *
 * Records user interactions (navigation, clicks, API calls) to an in-memory
 * buffer for deriving critical user journeys. Dev-only — not included in
 * production builds.
 *
 * Usage:
 *   - Call startTraceRecording() or startFresh() to begin recording
 *   - Call stopTraceRecording() to restore patched globals and stop
 *   - window.__uat_trace — the raw event array
 *   - window.__uat_dump() — pretty-print the trace to console
 *   - window.__uat_export() — copy trace JSON to clipboard
 *   - window.__uat_clear() — reset the trace
 */

import { z } from 'zod';
import { safeParseResponse } from '@shared/parse-json';
import { readStoredJson } from './readStoredJson';
import { TraceEventSchema, type TraceEvent } from '@shared/story-pipeline/trace-event';

export type { TraceEvent };

/** Minimal response shape — counts and identifiers only, never full objects. */
interface ResponseShape {
  artifactCount?: number;
  kinds?: string[];
  accountId?: string;
  totalCount?: number;
  pageSize?: number;
  hasOpportunities?: boolean;
}

// Response shape extractors — best-effort, never throw

/** Find the first array property in an object (shallow). */
function findArray(obj: Record<string, unknown>): unknown[] | null {
  for (const val of Object.values(obj)) {
    if (Array.isArray(val)) return val;
  }
  return null;
}

/** Collect unique kind strings from an array of objects. */
function collectKinds(items: unknown[]): string[] | undefined {
  const set = new Set<string>();
  for (const item of items) {
    if (item && typeof item === 'object') {
      const rec = item as Record<string, unknown>;
      const k = rec.kind ?? rec.blueprintKind;
      if (typeof k === 'string') set.add(k);
    }
  }
  return set.size > 0 ? [...set] : undefined;
}

/** Extract shape from /api/app-home/detail response. */
function extractDetailShape(data: unknown): ResponseShape | null {
  if (!data || typeof data !== 'object') return null;
  const obj = data as Record<string, unknown>;

  const items = findArray(obj);
  if (!items) return null;

  const shape: ResponseShape = { artifactCount: items.length };
  const kinds = collectKinds(items);
  if (kinds) shape.kinds = kinds;

  // Look for accountId at the top level
  const acctId = obj.accountId ?? obj.sfAccountId;
  if (typeof acctId === 'string') shape.accountId = acctId;

  return shape;
}

/** Extract shape from /api/app-home/list response. */
function extractListShape(data: unknown): ResponseShape | null {
  if (!data || typeof data !== 'object') return null;
  const obj = data as Record<string, unknown>;

  const items = findArray(obj);
  const shape: ResponseShape = {};

  // Total count — explicit field or array length
  if (typeof obj.total === 'number') shape.totalCount = obj.total;
  else if (typeof obj.totalCount === 'number') shape.totalCount = obj.totalCount;
  else if (items) shape.totalCount = items.length;

  // Page size
  if (typeof obj.pageSize === 'number') shape.pageSize = obj.pageSize;
  else if (typeof obj.limit === 'number') shape.pageSize = obj.limit;
  else if (items) shape.pageSize = items.length;

  if (items) {
    const kinds = collectKinds(items);
    if (kinds) shape.kinds = kinds;
  }

  // Only return if we extracted something useful
  return Object.keys(shape).length > 0 ? shape : null;
}

/** Route a URL to the appropriate shape extractor. */
function extractResponseShape(url: string, data: unknown): ResponseShape | null {
  if (url.includes('/api/app-home/detail')) return extractDetailShape(data);
  if (url.includes('/api/app-home/list')) return extractListShape(data);
  return null;
}

const STORAGE_KEY = 'uat-trace';

// Restore from sessionStorage on load (survives full-page navigations)
const trace: TraceEvent[] = (() => {
  try {
    return readStoredJson(sessionStorage, STORAGE_KEY, z.array(TraceEventSchema), []);
  } catch { return []; }
})();

let recording = false;
let cleanups: Array<() => void> = [];

function push(event: Omit<TraceEvent, 'ts'>) {
  trace.push({ ...event, ts: Date.now() });
  // Persist after every event so full-page navigations don't lose the trace
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trace)); } catch {}
}

// --- Navigation tracking ---
function trackNavigation() {
  let lastPath = window.location.pathname + window.location.search;

  const origPush = history.pushState.bind(history);
  const origReplace = history.replaceState.bind(history);

  history.pushState = (...args) => {
    origPush(...args);
    onLocationChange('push');
  };
  history.replaceState = (...args) => {
    origReplace(...args);
    onLocationChange('replace');
  };

  const onPopState = () => onLocationChange('pop');
  window.addEventListener('popstate', onPopState);

  function onLocationChange(trigger: string) {
    const newPath = window.location.pathname + window.location.search;
    if (newPath !== lastPath) {
      push({
        type: 'navigate',
        detail: { from: lastPath, to: newPath, trigger },
      });
      lastPath = newPath;
    }
  }

  cleanups.push(() => {
    history.pushState = origPush;
    history.replaceState = origReplace;
    window.removeEventListener('popstate', onPopState);
  });
}

// --- Click tracking ---
function trackClicks() {
  const handler = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target) return;

    // Walk up to find the nearest meaningful element
    const el = target.closest('[data-testid], button, a, [role="tab"], [role="option"], [role="menuitem"]') as HTMLElement | null;
    if (!el) return;

    const info: Record<string, unknown> = {};

    const testId = el.getAttribute('data-testid');
    if (testId) info.testid = testId;

    const role = el.getAttribute('role');
    if (role) info.role = role;

    const ariaLabel = el.getAttribute('aria-label');
    if (ariaLabel) info.ariaLabel = ariaLabel;

    // Get visible text (truncated)
    const text = el.textContent?.trim().slice(0, 80);
    if (text) info.text = text;

    // Tag name and href for links
    info.tag = el.tagName.toLowerCase();
    if (el.tagName === 'A') info.href = (el as HTMLAnchorElement).href;

    push({ type: 'click', detail: info });
  };

  document.addEventListener('click', handler, { capture: true });

  cleanups.push(() => {
    document.removeEventListener('click', handler, { capture: true });
  });
}

// --- API call tracking ---
function trackApiCalls() {
  const origFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input
      : input instanceof URL ? input.toString()
      : input instanceof Request ? input.url
      : String(input);

    // Only track API calls, not static assets
    if (!url.includes('/api/') && !url.includes('/auth/')) {
      return origFetch(input, init);
    }

    const method = init?.method ?? 'GET';
    const startMs = Date.now();

    try {
      const resp = await origFetch(input, init);
      const durationMs = Date.now() - startMs;
      const pathname = new URL(url, window.location.origin).pathname;

      // Best-effort response shape extraction — never delay or break the fetch
      let responseShape: ResponseShape | null = null;
      if (resp.ok) {
        try {
          const clone = resp.clone();
          const parsed = await safeParseResponse(z.unknown(), clone);
          if (parsed.success) responseShape = extractResponseShape(pathname, parsed.data);
        } catch {
          // Non-JSON or parse failure — shape stays null
        }
      }

      push({
        type: 'api',
        detail: {
          method,
          url: pathname,
          status: resp.status,
          durationMs,
          ...(responseShape && { responseShape }),
        },
      });
      return resp;
    } catch (err) {
      push({
        type: 'api',
        detail: {
          method,
          url: new URL(url, window.location.origin).pathname,
          error: String(err),
          durationMs: Date.now() - startMs,
        },
      });
      throw err;
    }
  };

  cleanups.push(() => {
    window.fetch = origFetch;
  });
}

// --- Public API on window ---
function expose() {
  const w = window as any;
  w.__uat_trace = trace;

  w.__uat_dump = () => {
    const groups: { route: string; events: TraceEvent[] }[] = [];
    let current: { route: string; events: TraceEvent[] } | null = null;

    for (const e of trace) {
      if (e.type === 'navigate') {
        current = { route: String(e.detail.to), events: [e] };
        groups.push(current);
      } else if (current) {
        current.events.push(e);
      }
    }

    console.group('UAT Trace — %d events, %d navigations', trace.length, groups.length);
    for (const g of groups) {
      console.group(g.route);
      for (const e of g.events) {
        if (e.type === 'click') {
          const d = e.detail;
          console.log('click:', d.testid ?? d.text ?? d.ariaLabel ?? d.tag);
        } else if (e.type === 'api') {
          console.log('api:', e.detail.method, e.detail.url, '→', e.detail.status);
        } else if (e.type === 'navigate') {
          console.log('→', e.detail.to);
        }
      }
      console.groupEnd();
    }
    console.groupEnd();
  };

  w.__uat_export = () => {
    void exportTrace();
    return JSON.stringify(trace, null, 2);
  };

  w.__uat_clear = () => {
    trace.length = 0;
    try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
    console.log('Trace cleared');
  };
}

async function exportTrace(): Promise<boolean> {
  const json = JSON.stringify(trace, null, 2);
  try {
    await navigator.clipboard.writeText(json);
    console.log('Trace copied to clipboard (%d events)', trace.length);
    return true;
  } catch {
    // Clipboard API blocked — fall back to textarea trick
    try {
      const ta = document.createElement('textarea');
      ta.value = json;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      console.log('Trace copied to clipboard via fallback (%d events)', trace.length);
      return true;
    } catch {
      console.log('Copy failed. Trace:\n', json);
      return false;
    }
  }
}

export function clearTrace(): void {
  trace.length = 0;
  try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
}

// --- Start recording ---
/** Return a copy of trace events. */
export function getTraceEvents(): TraceEvent[] {
  return [...trace];
}

/** Clear trace and start recording if not already started. */
export function startFresh(): void {
  clearTrace();
  startTraceRecording();
}

export function stopTraceRecording() {
  if (!recording) return;
  for (const cleanup of cleanups) cleanup();
  cleanups = [];
  recording = false;
}

function startTraceRecording() {
  if (recording) return;
  recording = true;

  push({ type: 'session-start', detail: { url: window.location.href, userAgent: navigator.userAgent } });
  trackNavigation();
  trackClicks();
  trackApiCalls();
  expose();

  console.log(
    '%c UAT Trace Recording ',
    'background: #6366f1; color: white; padding: 2px 8px; border-radius: 4px;',
    '— window.__uat_dump() to view, __uat_export() to copy JSON',
  );
}
